import { PaginationState, Row, SortingState } from '@tanstack/react-table'
import { TextInput } from 'components/form/inputs/TextInput'
import { TableComponent } from 'components/table/TableComponent'
import { AssetTypeFilter } from 'features/knowledgeDocumentViewer/components/toolbar/AssetTypeFilter'
import { Box } from 'features/knowledgeDocumentViewer/components/viewer/types'
import { useKnowledgeDocumentDataContext } from 'features/knowledgeDocumentViewer/contexts/KnowledgeDocumentDataContext'
import { useKnowledgeDocumentUXContext } from 'features/knowledgeDocumentViewer/contexts/KnowledgeDocumentUXContext'
import { useLinkedDocumentsNavigation } from 'features/knowledgeDocumentViewer/hooks/useLinkedDocumentsNavigation'
import {
  OcrResultWithPage,
  useOcrResults,
} from 'features/knowledgeDocumentViewer/hooks/useOcrResults'
import React, { useCallback, useEffect, useMemo, useState } from 'react'
import styled from 'styled-components'

import { FeedbackModal } from './FeedbackModal'
import { createOcrTableColumns } from './OcrTableColumns'

const StyledHeader = styled.header`
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: space-between;
  padding: 0.5rem;
`

export type OcrTableProps = {
  handleZoomToBox?: (id: string) => void
}

export const OcrTable: React.FC<OcrTableProps> = ({ handleZoomToBox }) => {
  const {
    currentDocumentQuery,
    activeTabId,
    currentPageNumber,
    currentDocumentId,
    currentSelectedBoxIds,
    updateDocumentHistoryItem,
    hoverId,
    handleMouseEnter,
    handleMouseLeave,
  } = useKnowledgeDocumentUXContext()

  const { siteID } = useKnowledgeDocumentDataContext()

  const [sorting, setSorting] = useState<SortingState>([])
  const [pagination, setPagination] = useState<PaginationState>({
    pageIndex: 0,
    pageSize: 50,
  })
  const [feedbackModalResult, setFeedbackModalResult] =
    useState<OcrResultWithPage | null>(null)

  const { filteredOcrResults } = useOcrResults()
  const { ocrResultRefsMap, navigateToLinkedDocuments } =
    useLinkedDocumentsNavigation()

  const handleSearch = useCallback(
    (searchInput: string) => {
      updateDocumentHistoryItem(activeTabId, { query: searchInput })
    },
    [updateDocumentHistoryItem, activeTabId]
  )

  const handleZoomToBoxClick = useCallback(
    (result: OcrResultWithPage) => {
      if (!handleZoomToBox) return

      const needsNavigation =
        result.pageNumber !== currentPageNumber &&
        result.pageNumber !== currentPageNumber + 1

      if (needsNavigation) {
        updateDocumentHistoryItem(activeTabId, {
          pageNumber: result.pageNumber,
          selectedBoxIds: [],
        })
        setTimeout(() => {
          const boxId = `${result.id}-0`
          handleZoomToBox(boxId)
        }, 500)
        return
      }

      const pageIndex = result.pageNumber === currentPageNumber + 1 ? 1 : 0
      const boxId = `${result.id}-${pageIndex}`
      handleZoomToBox(boxId)
    },
    [handleZoomToBox, currentPageNumber, updateDocumentHistoryItem, activeTabId]
  )

  const handleFlagClick = useCallback((result: OcrResultWithPage) => {
    setFeedbackModalResult(result)
  }, [])

  const getBoxIdForOcrResult = useCallback(
    (result: OcrResultWithPage): string => {
      const pageIndex = result.pageNumber === currentPageNumber ? 0 : 1
      return `${result.id}-${pageIndex}`
    },
    [currentPageNumber]
  )

  const handleViewLinkedClick = useCallback(
    (result: OcrResultWithPage, event: React.MouseEvent) => {
      const forceNewTab = event.metaKey || event.ctrlKey

      const boxId = getBoxIdForOcrResult(result)
      updateDocumentHistoryItem(activeTabId, { selectedBoxIds: [boxId] })

      navigateToLinkedDocuments({ ocrResult: result, forceNewTab })
    },
    [
      activeTabId,
      getBoxIdForOcrResult,
      navigateToLinkedDocuments,
      updateDocumentHistoryItem,
    ]
  )

  const columns = useMemo(
    () =>
      createOcrTableColumns({
        ocrResultRefsMap,
        onZoomToBox: handleZoomToBoxClick,
        onFlagClick: handleFlagClick,
        onViewLinkedClick: handleViewLinkedClick,
      }),
    [
      ocrResultRefsMap,
      handleZoomToBoxClick,
      handleFlagClick,
      handleViewLinkedClick,
    ]
  )

  const handleRowClick = useCallback(
    (row: Row<OcrResultWithPage>) => {
      const result = row.original
      const boxId = getBoxIdForOcrResult(result)

      if (currentSelectedBoxIds.includes(boxId)) {
        updateDocumentHistoryItem(activeTabId, { selectedBoxIds: [] })
      } else {
        updateDocumentHistoryItem(activeTabId, { selectedBoxIds: [boxId] })
      }
    },
    [
      activeTabId,
      currentSelectedBoxIds,
      getBoxIdForOcrResult,
      updateDocumentHistoryItem,
    ]
  )

  const { highlightedRowId, highlightedRowIndex } = useMemo(() => {
    if (currentSelectedBoxIds.length === 0) {
      return { highlightedRowId: null, highlightedRowIndex: -1 }
    }

    const selectedBoxId = currentSelectedBoxIds[0]
    const matchingIndex = filteredOcrResults.findIndex((result) => {
      const boxId = getBoxIdForOcrResult(result)
      return boxId === selectedBoxId
    })

    if (matchingIndex !== -1) {
      const matchingResult = filteredOcrResults[matchingIndex]
      return {
        highlightedRowId: `${matchingResult.id}-${matchingResult.pageNumber}`,
        highlightedRowIndex: matchingIndex,
      }
    }
    return { highlightedRowId: null, highlightedRowIndex: -1 }
  }, [currentSelectedBoxIds, filteredOcrResults, getBoxIdForOcrResult])

  const hoveredRowId = useMemo(() => {
    if (!hoverId) return null

    const matchingResult = filteredOcrResults.find((result) => {
      const boxId = getBoxIdForOcrResult(result)
      return boxId === hoverId
    })

    if (matchingResult) {
      return `${matchingResult.id}-${matchingResult.pageNumber}`
    }
    return null
  }, [hoverId, filteredOcrResults, getBoxIdForOcrResult])

  const handleRowHoverStart = useCallback(
    (row: Row<OcrResultWithPage>) => {
      const result = row.original
      const boxId = getBoxIdForOcrResult(result)

      const mockBox: Box = {
        id: boxId,
        label: result.text ?? '',
        type: result.type,
        confirmed: result.confirmed ?? false,
        x: 0,
        y: 0,
        w: 0,
        h: 0,
      }
      handleMouseEnter(mockBox)
    },
    [getBoxIdForOcrResult, handleMouseEnter]
  )

  const handleRowHoverEnd = useCallback(() => {
    handleMouseLeave()
  }, [handleMouseLeave])

  useEffect(() => {
    if (highlightedRowId && highlightedRowIndex !== -1) {
      const targetPageIndex = Math.floor(
        highlightedRowIndex / pagination.pageSize
      )

      if (targetPageIndex !== pagination.pageIndex) {
        setPagination((prev) => ({ ...prev, pageIndex: targetPageIndex }))
      }

      // Small delay to ensure the table has rendered after page change
      const timeoutId = setTimeout(() => {
        const rowElement = document.getElementById(highlightedRowId)
        if (rowElement) {
          rowElement.scrollIntoView({ behavior: 'smooth', block: 'nearest' })
        }
      }, 100)
      return () => clearTimeout(timeoutId)
    }
  }, [
    highlightedRowId,
    highlightedRowIndex,
    pagination.pageSize,
    pagination.pageIndex,
  ])

  return (
    <div>
      <StyledHeader>
        <AssetTypeFilter />
        <TextInput
          placeholder='Enter Search Query'
          value={currentDocumentQuery}
          onChange={(e) => handleSearch(e.target.value)}
        />
      </StyledHeader>
      <TableComponent
        data={filteredOcrResults}
        columns={columns}
        isLoadingData={false}
        footerName='OCR Results'
        pagination={pagination}
        setPagination={setPagination}
        sorting={sorting}
        setSorting={setSorting}
        showFilterBar={false}
        showTable={true}
        showFooter={true}
        cardStyleRows={false}
        cellPadding='0 0.25rem'
        cellFontSize='0.75rem'
        maxHeight='calc(100vh - 500px)'
        getRowId={(row) => `${row.id}-${row.pageNumber}`}
        onRowClick={handleRowClick}
        highlightSelectedRow={true}
        controlledHighlightedRowId={highlightedRowId}
        onRowHoverStart={handleRowHoverStart}
        onRowHoverEnd={handleRowHoverEnd}
        controlledHoveredRowId={hoveredRowId}
      />
      {feedbackModalResult && (
        <FeedbackModal
          feedbackType='asset'
          isOpen={feedbackModalResult !== null}
          onClose={() => setFeedbackModalResult(null)}
          boxId={String(feedbackModalResult.id)}
          boxLabel={feedbackModalResult.text ?? null}
          boxType={feedbackModalResult.type ?? null}
          documentId={currentDocumentId}
          siteId={siteID}
        />
      )}
    </div>
  )
}
